import { useEffect, useRef, useState } from 'react';
import { calendarApi, customerApi, settingsApi } from '../api';
import { Modal, dateTh, nowHhmm, todayIso, useLoader } from './common';
import { IconSearch, IconCalendar, IconClock, IconGuest } from './icons';

const DEFAULT_OPEN_TIME = '10:00';
const DEFAULT_CLOSE_TIME = '23:00';

const CHANNELS = [
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'booking', label: 'Booking' },
  { value: 'agency', label: 'Agency' },
];

const GENDERS = [
  { value: 'female', glyph: '♀', className: 'g-female' },
  { value: 'male', glyph: '♂', className: 'g-male' },
  { value: 'other', glyph: '⚧', className: 'g-other' },
];

/**
 * Step 1 of จองเลย — ข้อมูลการจอง.
 *
 * Channel, when, and who booked. Every customer field is deliberately optional:
 * a walk-in who gives no name still has to be bookable, so ต่อไป is never
 * blocked by them (the real POS behaves the same way).
 *
 * `customer` seeds the guest, for the Book button on a member's own page: the
 * member is already known there, so reception should not have to search for
 * somebody they just opened. The search box is left empty on purpose — filling
 * it would fire a lookup and drop a typeahead over the form on open.
 */
export default function BookingInfoModal({ date, customer, onClose, onNext }) {
  const lookups = useLoader(() => calendarApi.lookups(), []);
  const channelSettings = useLoader(() => settingsApi.bookingChannels(), []);
  // This screen follows SAANTI's operating window directly. It must not inherit
  // a stale branch value cached by a session that was opened before hours were
  // changed in settings.
  const openTime = DEFAULT_OPEN_TIME;
  const closeTime = DEFAULT_CLOSE_TIME;
  const constrainTime = (value) => {
    if (!value) return value;
    if (value < openTime) return openTime;
    if (value > closeTime) return closeTime;
    return value;
  };

  const [channel, setChannel] = useState('walk_in');
  const [agencyId, setAgencyId] = useState('');
  const [agencyRef, setAgencyRef] = useState('');
  const [agencyError, setAgencyError] = useState('');
  const [bookingDate, setBookingDate] = useState(date || todayIso());
  const [time, setTime] = useState(() => constrainTime(nowHhmm()));
  const [timeError, setTimeError] = useState('');
  const [bookerIsGuest1, setBookerIsGuest1] = useState(true);
  const [guest, setGuest] = useState({
    customerId: customer?.id || null,
    name: customer?.nickname || customer?.firstName || '',
    phone: customer?.phone || '',
    // 'unspecified' is how the customer record says nothing was asked, which is
    // this form's empty, not a fourth choice of its own.
    gender: customer?.gender === 'unspecified' ? '' : customer?.gender || '',
    ageBandId: customer?.ageBandId || '',
    nationalityCode: customer?.nationalityCode || '',
  });
  const [note, setNote] = useState('');
  const [showGuestDetail, setShowGuestDetail] = useState(false);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return undefined; }
    const id = setTimeout(() => {
      customerApi.search(q).then((r) => setHits(r.customers || [])).catch(() => setHits([]));
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setHits([]);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const next = () => {
    if (channel === 'agency' && !agencyId) {
      setAgencyError('กรุณาเลือกเอเจนซี่');
      return;
    }
    if (channel === 'agency' && channelSettings.data?.forceAgencyCode && !agencyRef.trim()) {
      setAgencyError('กรุณากรอกรหัสเอเจนซี่');
      return;
    }
    if (!time || time < openTime || time > closeTime) {
      setTimeError(`เลือกเวลาได้ตั้งแต่ ${openTime} ถึง ${closeTime} เท่านั้น`);
      return;
    }
    onNext({
      channel,
      agencyId: agencyId || null,
      agencyName: (lookups.data?.agencies || []).find(
        (agency) => String(agency.id) === String(agencyId)
      )?.name || null,
      agencyRef: agencyRef.trim() || null,
      date: bookingDate,
      time,
      note,
      bookerIsGuest1,
      guest,
    });
  };

  return (
    <Modal
      title="ข้อมูลการจอง"
      onClose={onClose}
      footer={
        <>
          <button className="btn-outline" onClick={onClose}>ยกเลิก</button>
          {/* Never disabled — customer details are optional. */}
          <button className="btn-solid" onClick={next}>ต่อไป</button>
        </>
      }
    >
      <div className="field">
        <label>เลือกช่องทางการจอง</label>
        <div className="segmented">
          {CHANNELS.map((c) => (
            <button
              key={c.value}
              className={`seg${channel === c.value ? ' on' : ''}`}
              onClick={() => {
                setChannel(c.value);
                setAgencyError('');
              }}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {channel === 'agency' && (
        <>
          <div className="field">
            <label>เอเจนซี่ *</label>
            <select
              value={agencyId}
              onChange={(e) => { setAgencyId(e.target.value); setAgencyError(''); }}
            >
              <option value="">เลือกเอเจนซี่…</option>
              {(lookups.data?.agencies || []).map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          {channelSettings.data?.forceAgencyCode && (
            <div className="field">
              <label>รหัสเอเจนซี่ *</label>
              <input
                value={agencyRef}
                onChange={(event) => { setAgencyRef(event.target.value); setAgencyError(''); }}
                placeholder="กรอกรหัสเอเจนซี่ / Voucher / เลขห้อง"
              />
            </div>
          )}
          {agencyError && <div className="login-error">{agencyError}</div>}
        </>
      )}

      <div className="field">
        <label>เวลาจอง</label>
        <div className="when-row">
          <label className="date-box">
            <span>{dateTh(bookingDate)}</span>
            <IconCalendar size={17} />
            <input
              type="date"
              value={bookingDate}
              onChange={(e) => setBookingDate(e.target.value)}
            />
          </label>
          <label className="date-box time">
            <span>{time}</span>
            <IconClock size={17} />
            <input
              type="time"
              min={openTime}
              max={closeTime}
              value={time}
              aria-label={`เวลาจอง ${openTime} ถึง ${closeTime}`}
              onInput={(e) => {
                const constrained = constrainTime(e.currentTarget.value);
                // When the corrected value equals the previous React state,
                // React legitimately skips a render. Update the native field
                // too so a typed 09:30 cannot remain visible as 09:30.
                e.currentTarget.value = constrained;
                setTime(constrained);
                setTimeError('');
              }}
            />
          </label>
        </div>
        {timeError && <div className="login-error" style={{ marginTop: 8 }}>{timeError}</div>}
      </div>

      <div className="booker-head">
        <span>ใส่ชื่อผู้จอง</span>
        <label className={`radio${bookerIsGuest1 ? ' on' : ''}`}>
          <input
            type="checkbox"
            checked={bookerIsGuest1}
            onChange={(e) => setBookerIsGuest1(e.target.checked)}
          />
          ข้อมูลผู้จองตรงกับลูกค้าคนที่ 1
        </label>
        <button
          className="btn-ghost btn-sm"
          style={{ marginLeft: 'auto' }}
          onClick={() => setShowGuestDetail((v) => !v)}
        >
          <IconGuest size={15} /> {showGuestDetail ? 'ซ่อนรายละเอียดลูกค้า' : 'กรอกรายละเอียดลูกค้า'}
        </button>
      </div>

      <div className="field" ref={boxRef} style={{ position: 'relative' }}>
        <label className="search-field">
          <IconSearch size={17} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ลูกค้า"
          />
        </label>
        {hits.length > 0 && (
          <div className="typeahead">
            {hits.map((c) => (
              <button
                type="button"
                key={c.id}
                onClick={() => {
                  setGuest({
                    ...guest,
                    customerId: c.id,
                    name: c.nickname || c.first_name || '',
                    phone: c.phone || '',
                  });
                  setQ(c.nickname || c.first_name || '');
                  setHits([]);
                }}
              >
                <strong>{c.nickname || c.first_name || 'ลูกค้า'}</strong>
                <span>#{c.member_code} · {c.phone || '-'}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid-2">
        <div className="field">
          <input
            value={guest.phone}
            onChange={(e) => setGuest({ ...guest, phone: e.target.value })}
            placeholder="เบอร์โทรศัพท์"
          />
        </div>
        <div className="field">
          <input
            value={guest.name}
            onChange={(e) => setGuest({ ...guest, name: e.target.value })}
            placeholder="ลูกค้า"
          />
        </div>
      </div>

      <div className="ident-row">
        {GENDERS.map((g) => (
          <button
            key={g.value}
            className={`gender-btn ${g.className}${guest.gender === g.value ? ' on' : ''}`}
            title={g.value}
            onClick={() => setGuest({
              ...guest,
              gender: guest.gender === g.value ? '' : g.value,
            })}
          >
            {g.glyph}
          </button>
        ))}

        <select
          value={guest.ageBandId}
          onChange={(e) => setGuest({ ...guest, ageBandId: e.target.value })}
        >
          <option value="">อายุ</option>
          {(lookups.data?.ageBands || []).map((b) => (
            <option key={b.id} value={b.id}>{b.label_th || b.label_en}</option>
          ))}
        </select>

        <select
          value={guest.nationalityCode}
          onChange={(e) => setGuest({ ...guest, nationalityCode: e.target.value })}
        >
          <option value="">สัญชาติ</option>
          {(lookups.data?.nationalities || []).map((n) => (
            <option key={n.code} value={n.code}>{n.name_th || n.name_en}</option>
          ))}
        </select>
      </div>

      {/* The fuller customer record — only worth the space when reception is
          actually creating a member rather than seating a walk-in. */}
      {showGuestDetail && (
        <div className="guest-detail">
          <div className="grid-2">
            <div className="field">
              <label>ชื่อจริง</label>
              <input
                value={guest.firstName || ''}
                onChange={(e) => setGuest({ ...guest, firstName: e.target.value })}
              />
            </div>
            <div className="field">
              <label>นามสกุล</label>
              <input
                value={guest.lastName || ''}
                onChange={(e) => setGuest({ ...guest, lastName: e.target.value })}
              />
            </div>
          </div>
          <div className="grid-2">
            <div className="field">
              <label>อีเมล</label>
              <input
                type="email"
                value={guest.email || ''}
                onChange={(e) => setGuest({ ...guest, email: e.target.value })}
              />
            </div>
            <div className="field">
              <label>วันเกิด</label>
              <input
                type="date"
                value={guest.birthday || ''}
                onChange={(e) => setGuest({ ...guest, birthday: e.target.value })}
              />
            </div>
          </div>
          <div className="field">
            <label>อาชีพ</label>
            <select
              value={guest.occupationId || ''}
              onChange={(e) => setGuest({ ...guest, occupationId: e.target.value })}
            >
              <option value="">ไม่ระบุ</option>
              {(lookups.data?.occupations || []).map((o) => (
                <option key={o.id} value={o.id}>{o.name_th || o.name_en}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>ความต้องการพิเศษ</label>
            <input
              value={guest.requirement || ''}
              onChange={(e) => setGuest({ ...guest, requirement: e.target.value })}
              placeholder="เช่น น้ำหนักมือเบา, แพ้น้ำมันบางชนิด"
            />
          </div>
        </div>
      )}

      <div className="field">
        <label>หมายเหตุ <span style={{ color: 'var(--ink-400)', fontWeight: 400 }}>(หมายเหตุเฉพาะการจองนี้)</span></label>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="หมายเหตุ" />
      </div>
    </Modal>
  );
}
